import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { MIN_REASON } from '@urbanrats/ops-core';
import { PageHead, Section } from '../components/common.tsx';
import { fmtTime, useCtx } from '../lib.ts';

interface Tier {
  rankFrom: number;
  rankTo: number;
  kind: 'FREEBET' | 'BOOST';
  count: number;
  values: Record<string, string>;
  boostBps: number | null;
  expiresDays?: number;
}
interface Board {
  boardId: string;
  name: string;
  metric: string;
  period: string;
  scope: string;
  scopeId: string;
  currency: string | null;
  minRounds: number;
  funding: string;
  approvalLimits: Record<string, string>;
  status: string;
  current: { start: string; end: string; top: { rank: number; alias: string; operatorId: string; brandId: string; country: string | null; value: string; rounds: number }[] };
  prizes: Tier[];
}
interface DropV {
  dropId: string;
  name: string;
  startsAt: string;
  endsAt: string;
  metric: string;
  target: number;
  randomized: boolean;
  maxOpens: number;
  opens: number;
  status: string;
  seedCommitment: string;
  seed: string | null;
  scope: string;
  scopeId: string;
  funding: string;
  prizeTable: { weight: number; kind: string; count: number; values?: Record<string, string>; boostBps?: number | null }[];
}
interface Run {
  runId: string;
  boardId: string;
  periodStart: string;
  periodEnd: string;
  status: string;
  winners: { rank: number; alias: string; operatorId: string; currency: string | null; kind: string; count: number; faceValueAtomic: string | null; skipped: string | null }[];
  totals: Record<string, string>;
  funding: string;
  createdAt: string;
}
interface View {
  canManage: boolean;
  canApprove: boolean;
  canSwitch: boolean;
  metrics: string[];
  dropMetrics: string[];
  switches: { scope: string; scopeId: string; feature: string; enabled: boolean; blockedCountries: string[]; updatedBy: string | null; updatedAt: string }[];
  boards: Board[];
  drops: DropV[];
  runs: Run[];
  cost30d: { funding: string; currency: string; atomic: string; items: number }[];
  scopes: { operator_id: string; name: string; brand_id: string | null; brand_name: string | null; wallet_currency: string | null }[];
}
type Post = <T>(path: string, body: Record<string, unknown>) => Promise<{ ok: true; value: T } | { ok: false; error: string }>;

const FEATURES = [
  ['LEADERBOARDS', 'Leaderboards'],
  ['PRIZES', 'Leaderboard prizes'],
  ['DROPS', 'Drop chests'],
] as const;
const METRIC_LABEL: Record<string, string> = {
  ROUNDS: 'Rounds played',
  PLAY_TIME: 'Time played',
  BEST_MULTIPLIER: 'Best multiplier',
  EXACT_HITS: 'Exact hits',
  WINS: 'Winning bets',
  ACTIVE_DAYS: 'Active days',
  STAKE: 'Total stake (one currency)',
  NET_WIN: 'Net win (one currency)',
  BIGGEST_WIN: 'Biggest win (one currency)',
};
const MONEY = new Set(['STAKE', 'NET_WIN', 'BIGGEST_WIN']);
const valueText = (metric: string, v: string) => (metric === 'PLAY_TIME' ? `${Math.round(Number(v) / 60)} min` : metric === 'BEST_MULTIPLIER' ? `×${(Number(v) / 10_000).toFixed(2)}` : v);
const valuesText = (v: Record<string, string> | undefined) =>
  Object.entries(v ?? {})
    .map(([k, x]) => `${k} ${x}`)
    .join(' · ');
/** "EUR 500 TRY 20000" → {EUR: "500", TRY: "20000"} (atomic units). */
const parseValues = (s: string) => {
  const out: Record<string, string> = {};
  const parts = s.trim().split(/[\s,:]+/).filter(Boolean);
  for (let i = 0; i + 1 < parts.length; i += 2) out[parts[i]!.toUpperCase()] = parts[i + 1]!;
  return out;
};

/**
 * Rewards and leaderboards: switches (global / operator / site, blocked countries), leaderboards with
 * stepped prizes, drop chests with a committed seed, prize runs waiting for approval and 30-day cost.
 */
export function Engagement() {
  const { backend } = useCtx();
  const server = backend.server;
  const [v, setV] = useState<View | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const load = useCallback(async () => {
    if (!server) return;
    const r = await server.get<View>('/v1/admin/engagement');
    if (r.ok) {
      setV(r.value);
      setErr(null);
    } else setErr(r.error);
  }, [server]);
  useEffect(() => {
    const first = setTimeout(() => void load(), 0);
    const t = setInterval(() => void load(), 20_000);
    return () => (clearTimeout(first), clearInterval(t));
  }, [load]);
  if (!server) return <p className="empty">Rewards and leaderboards need the UrbanRats backend.</p>;
  if (!v) return <p className="empty">{err ?? 'Loading…'}</p>;
  const post: Post = (p, b) => server.post(p, b);
  const pending = v.runs.filter((r) => r.status === 'PENDING_APPROVAL');
  return (
    <div className="eng" data-testid="engagement">
      <PageHead title="Rewards and leaderboards">
        Leaderboards across every operator and site, stepped prizes and drop chests. Prizes are freebets or boosts in the player&apos;s wallet currency; nothing here changes counts, odds or results. A feature runs only where the global, operator and site switches all allow it.
      </PageHead>
      {pending.length > 0 && (
        <Section title={`Waiting for approval (${pending.length})`}>
          {pending.map((r) => (
            <RunCard key={r.runId} r={r} v={v} post={post} reload={load} />
          ))}
        </Section>
      )}
      <Switches v={v} post={post} reload={load} />
      <Boards v={v} post={post} reload={load} />
      <Drops v={v} post={post} reload={load} />
      <Section title="Prize runs">
        {v.runs.length === 0 ? (
          <p className="empty">No prize run yet. A run starts when a period of a board with prizes ends.</p>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Board</th>
                  <th>Period</th>
                  <th>Winners</th>
                  <th>Total</th>
                  <th>Funding</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {v.runs.map((r) => (
                  <tr key={r.runId}>
                    <td>{v.boards.find((b) => b.boardId === r.boardId)?.name ?? r.boardId}</td>
                    <td className="muted">
                      {fmtTime(r.periodStart)} → {fmtTime(r.periodEnd)}
                    </td>
                    <td>
                      {r.winners.filter((w) => !w.skipped).length} / {r.winners.length}
                    </td>
                    <td className="mono">{valuesText(r.totals) || '—'}</td>
                    <td>{r.funding}</td>
                    <td>
                      <span className={`tag st-${r.status === 'DONE' ? 'SETTLED' : r.status === 'REJECTED' ? 'VOIDED' : 'PENDING'}`}>{r.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
      <Section title="Cost (last 30 days, per currency)">
        {v.cost30d.length === 0 ? (
          <p className="empty">Nothing granted yet.</p>
        ) : (
          <div className="kpis">
            {v.cost30d.map((c) => (
              <div key={`${c.funding}/${c.currency}`} className="kpi">
                <span className="kpi-label">
                  {c.funding === 'URBANRATS' ? 'Paid by UrbanRats' : 'Paid by operators'} · {c.currency}
                </span>
                <strong className="mono">{c.atomic}</strong>
                <span className="muted">atomic units of freebet face value</span>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function useReason() {
  const [reason, setReason] = useState('');
  return { reason, setReason, ok: reason.trim().length >= MIN_REASON };
}
function F({ label, hint, children }: { label: string; hint?: string; children: ReactNode }) {
  return (
    <label className="op-field">
      <span>{label}</span>
      {children}
      {hint && <small className="muted">{hint}</small>}
    </label>
  );
}
function ScopePicker({ v, scope, scopeId, set }: { v: View; scope: string; scopeId: string; set: (s: string, id: string) => void }) {
  const ops = [...new Map(v.scopes.map((s) => [s.operator_id, s.name])).entries()];
  return (
    <>
      <F label="Scope">
        <select value={scope} onChange={(e) => set(e.target.value, e.target.value === 'GLOBAL' ? '*' : '')}>
          <option value="GLOBAL">Global (every operator)</option>
          <option value="OPERATOR">One operator</option>
          <option value="BRAND">One site</option>
        </select>
      </F>
      {scope === 'OPERATOR' && (
        <F label="Operator">
          <select value={scopeId} onChange={(e) => set(scope, e.target.value)}>
            <option value="">choose…</option>
            {ops.map(([id, n]) => (
              <option key={id} value={id}>
                {n} · {id}
              </option>
            ))}
          </select>
        </F>
      )}
      {scope === 'BRAND' && (
        <F label="Site">
          <select value={scopeId} onChange={(e) => set(scope, e.target.value)}>
            <option value="">choose…</option>
            {v.scopes
              .filter((s) => s.brand_id)
              .map((s) => (
                <option key={`${s.operator_id}/${s.brand_id}`} value={`${s.operator_id}/${s.brand_id}`}>
                  {s.brand_name} · {s.operator_id}/{s.brand_id} ({s.wallet_currency})
                </option>
              ))}
          </select>
        </F>
      )}
    </>
  );
}

function Switches({ v, post, reload }: { v: View; post: Post; reload: () => Promise<void> }) {
  const [scope, setScope] = useState('GLOBAL');
  const [scopeId, setScopeId] = useState('*');
  const { reason, setReason, ok } = useReason();
  const [msg, setMsg] = useState<string | null>(null);
  const row = (feature: string, sc = scope, id = scopeId) => v.switches.find((s) => s.scope === sc && s.scopeId === id && s.feature === feature);
  const [blocked, setBlocked] = useState<Record<string, string>>({});
  return (
    <Section title="Switches">
      <p className="note">Global is the master switch. An operator or site row can only turn a feature off (or block countries) where the global switch allows it; no row = allowed.</p>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Where</th>
              {FEATURES.map(([f, l]) => (
                <th key={f}>{l}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...new Set(v.switches.map((s) => `${s.scope}|${s.scopeId}`))].map((k) => {
              const [sc, id] = k.split('|') as [string, string];
              return (
                <tr key={k}>
                  <td>
                    <b>{sc === 'GLOBAL' ? 'Global' : sc === 'OPERATOR' ? 'Operator' : 'Site'}</b> <span className="mono muted">{id === '*' ? '' : id}</span>
                  </td>
                  {FEATURES.map(([f]) => {
                    const r = row(f, sc, id);
                    return (
                      <td key={f}>
                        {r ? (
                          <span className={`tag ${r.enabled ? 'st-SETTLED' : 'st-VOIDED'}`} title={r.updatedBy ? `by ${r.updatedBy} · ${fmtTime(r.updatedAt)}` : undefined}>
                            {r.enabled ? 'ON' : 'OFF'}
                            {r.blockedCountries.length ? ` · not in ${r.blockedCountries.join(' ')}` : ''}
                          </span>
                        ) : (
                          <span className="muted">inherit</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {v.canSwitch && (
        <div className="op-form">
          <div className="op-grid">
            <ScopePicker v={v} scope={scope} scopeId={scopeId} set={(s, id) => (setScope(s), setScopeId(id))} />
            <F label={`Reason (min ${MIN_REASON} chars)`}>
              <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="sw-reason" />
            </F>
          </div>
          <div className="eng-switch-row">
            {FEATURES.map(([f, l]) => {
              const r = row(f);
              return (
                <div key={f} className="eng-switch">
                  <strong>{l}</strong>
                  <input placeholder="blocked countries, e.g. US GB" value={blocked[f] ?? r?.blockedCountries.join(' ') ?? ''} onChange={(e) => setBlocked({ ...blocked, [f]: e.target.value })} />
                  <div className="op-actions">
                    {[true, false].map((on) => (
                      <button
                        key={String(on)}
                        type="button"
                        className={`btn ${on ? 'primary' : 'danger'}`}
                        disabled={!ok || !scopeId}
                        data-testid={`sw-${f}-${on ? 'on' : 'off'}`}
                        onClick={async () => {
                          const res = await post('/v1/admin/engagement/switches', { scope, scopeId, feature: f, enabled: on, blockedCountries: (blocked[f] ?? r?.blockedCountries.join(' ') ?? '').split(/[\s,]+/).filter(Boolean), reason });
                          setMsg(res.ok ? `${l}: ${on ? 'on' : 'off'} for ${scope === 'GLOBAL' ? 'everyone' : scopeId}` : res.error);
                          if (res.ok) await reload();
                        }}
                      >
                        {on ? 'Turn on' : 'Turn off'}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          {msg && <p className="op-msg ok">{msg}</p>}
        </div>
      )}
    </Section>
  );
}

function TiersEditor({ tiers, set, currencyHint }: { tiers: Tier[]; set: (t: Tier[]) => void; currencyHint: string }) {
  const [raw, setRaw] = useState<string[]>(() => tiers.map((t) => valuesText(t.values).replace(/ · /g, ' ')));
  const up = (i: number, p: Partial<Tier>) => set(tiers.map((t, j) => (j === i ? { ...t, ...p } : t)));
  return (
    <div className="eng-tiers">
      {tiers.map((t, i) => (
        <div key={i} className="eng-tier">
          <input inputMode="numeric" value={t.rankFrom} onChange={(e) => up(i, { rankFrom: Number(e.target.value) || 1 })} aria-label="From rank" />
          <span>–</span>
          <input inputMode="numeric" value={t.rankTo} onChange={(e) => up(i, { rankTo: Number(e.target.value) || 1 })} aria-label="To rank" />
          <select value={t.kind} onChange={(e) => up(i, { kind: e.target.value as Tier['kind'] })} aria-label="Kind">
            <option value="FREEBET">Freebet</option>
            <option value="BOOST">Boost</option>
          </select>
          <input inputMode="numeric" value={t.count} onChange={(e) => up(i, { count: Number(e.target.value) || 1 })} aria-label="Count" title="number of items" />
          {t.kind === 'FREEBET' ? (
            <input
              value={raw[i] ?? ''}
              placeholder={`face value per currency (atomic), e.g. ${currencyHint}`}
              onChange={(e) => {
                const r = [...raw];
                r[i] = e.target.value;
                setRaw(r);
                up(i, { values: parseValues(e.target.value) });
              }}
              aria-label="Values"
            />
          ) : (
            <input inputMode="numeric" value={t.boostBps ?? ''} placeholder="boost bps (1000 = +10 %)" onChange={(e) => up(i, { boostBps: Number(e.target.value) || null })} aria-label="Boost bps" />
          )}
          <button type="button" className="btn ghost" onClick={() => (set(tiers.filter((_, j) => j !== i)), setRaw(raw.filter((_, j) => j !== i)))}>
            ✕
          </button>
        </div>
      ))}
      <button type="button" className="btn" onClick={() => (set([...tiers, { rankFrom: (tiers.at(-1)?.rankTo ?? 0) + 1, rankTo: (tiers.at(-1)?.rankTo ?? 0) + 1, kind: 'FREEBET', count: 1, values: {}, boostBps: null }]), setRaw([...raw, '']))}>
        + Prize step
      </button>
    </div>
  );
}

function Boards({ v, post, reload }: { v: View; post: Post; reload: () => Promise<void> }) {
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ name: '', metric: 'ROUNDS', period: 'WEEKLY', scope: 'GLOBAL', scopeId: '*', currency: 'EUR', minRounds: '5', funding: 'URBANRATS', approval: '', startsAt: '', endsAt: '' });
  const [tiers, setTiers] = useState<Tier[]>([{ rankFrom: 1, rankTo: 1, kind: 'FREEBET', count: 5, values: {}, boostBps: null }]);
  const { reason, setReason, ok } = useReason();
  const [msg, setMsg] = useState<string | null>(null);
  const currencies = [...new Set(v.scopes.map((s) => s.wallet_currency).filter(Boolean))] as string[];
  return (
    <Section title={`Leaderboards (${v.boards.length})`}>
      <div className="eng-boards">
        {v.boards.map((b) => (
          <div key={b.boardId} className="eng-board" data-testid={`board-${b.boardId}`}>
            <div className="eng-board-head">
              <strong>{b.name}</strong>
              <span className={`tag st-${b.status === 'ACTIVE' ? 'SETTLED' : 'PENDING'}`}>{b.status}</span>
            </div>
            <span className="muted">
              {METRIC_LABEL[b.metric]} · {b.period.toLowerCase()} · {b.scope === 'GLOBAL' ? 'global' : b.scopeId} · min {b.minRounds} round(s) · {b.funding === 'URBANRATS' ? 'UrbanRats pays' : 'operator pays'}
            </span>
            <span className="muted">
              now: {fmtTime(b.current.start)} → {fmtTime(b.current.end)}
            </span>
            <ol className="eng-top">
              {b.current.top.length === 0 && <li className="muted">nobody yet</li>}
              {b.current.top.map((r) => (
                <li key={r.rank}>
                  <span>#{r.rank}</span> <span>{r.alias}</span> <span className="muted">{r.operatorId}/{r.brandId}</span> <b>{valueText(b.metric, r.value)}</b>
                </li>
              ))}
            </ol>
            {b.prizes.length > 0 && (
              <ul className="eng-prizes">
                {b.prizes.map((p) => (
                  <li key={p.rankFrom}>
                    #{p.rankFrom}
                    {p.rankTo > p.rankFrom ? `–${p.rankTo}` : ''}: {p.count} × {p.kind === 'FREEBET' ? `freebet (${valuesText(p.values)})` : `+${(p.boostBps ?? 0) / 100} % boost`}
                  </li>
                ))}
              </ul>
            )}
            {v.canManage && (
              <div className="op-actions">
                {(['ACTIVE', 'PAUSED', 'ARCHIVED'] as const)
                  .filter((s) => s !== b.status)
                  .map((s) => (
                    <button
                      key={s}
                      type="button"
                      className="btn ghost"
                      onClick={async () => {
                        const r = prompt(`${s === 'ACTIVE' ? 'Resume' : s === 'PAUSED' ? 'Pause' : 'Archive'} “${b.name}”. Reason (min ${MIN_REASON} chars):`);
                        if (!r || r.trim().length < MIN_REASON) return;
                        const res = await post(`/v1/admin/engagement/boards/${b.boardId}`, { status: s, reason: r });
                        if (!res.ok) alert(res.error);
                        await reload();
                      }}
                    >
                      {s === 'ACTIVE' ? 'Resume' : s === 'PAUSED' ? 'Pause' : 'Archive'}
                    </button>
                  ))}
              </div>
            )}
          </div>
        ))}
      </div>
      {v.canManage && !adding && (
        <button type="button" className="btn primary" onClick={() => setAdding(true)} data-testid="board-new">
          + New leaderboard
        </button>
      )}
      {v.canManage && adding && (
        <form
          className="op-form op-site-form"
          onSubmit={async (e) => {
            e.preventDefault();
            const r = await post('/v1/admin/engagement/boards', {
              name: f.name,
              metric: f.metric,
              period: f.period,
              startsAt: f.startsAt ? new Date(f.startsAt).toISOString() : undefined,
              endsAt: f.endsAt ? new Date(f.endsAt).toISOString() : undefined,
              scope: f.scope,
              scopeId: f.scopeId,
              currency: MONEY.has(f.metric) ? f.currency : undefined,
              minRounds: Number(f.minRounds) || 1,
              funding: f.funding,
              approvalLimits: parseValues(f.approval),
              prizes: tiers,
              reason,
            });
            setMsg(r.ok ? null : r.error);
            if (r.ok) {
              setAdding(false);
              setReason('');
              await reload();
            }
          }}
        >
          <h4>New leaderboard</h4>
          <div className="op-grid">
            <F label="Name (players see it)">
              <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Haftanın en aktifleri" data-testid="board-name" />
            </F>
            <F label="Ranked by">
              <select value={f.metric} onChange={(e) => setF({ ...f, metric: e.target.value })}>
                {v.metrics.map((m) => (
                  <option key={m} value={m}>
                    {METRIC_LABEL[m]}
                  </option>
                ))}
              </select>
            </F>
            {MONEY.has(f.metric) && (
              <F label="Currency" hint="money is never added across currencies">
                <select value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })}>
                  {currencies.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </F>
            )}
            <F label="Period">
              <select value={f.period} onChange={(e) => setF({ ...f, period: e.target.value })}>
                <option value="DAILY">Daily (UTC)</option>
                <option value="WEEKLY">Weekly (Mon–Sun, UTC)</option>
                <option value="MONTHLY">Monthly</option>
                <option value="CUSTOM">Custom dates</option>
              </select>
            </F>
            {f.period === 'CUSTOM' && (
              <>
                <F label="From">
                  <input type="datetime-local" value={f.startsAt} onChange={(e) => setF({ ...f, startsAt: e.target.value })} />
                </F>
                <F label="To">
                  <input type="datetime-local" value={f.endsAt} onChange={(e) => setF({ ...f, endsAt: e.target.value })} />
                </F>
              </>
            )}
            <ScopePicker v={v} scope={f.scope} scopeId={f.scopeId} set={(s, id) => setF({ ...f, scope: s, scopeId: id })} />
            <F label="Minimum rounds to rank" hint="keeps one-bet players out">
              <input inputMode="numeric" value={f.minRounds} onChange={(e) => setF({ ...f, minRounds: e.target.value })} />
            </F>
            <F label="Who pays the prizes">
              <select value={f.funding} onChange={(e) => setF({ ...f, funding: e.target.value })}>
                <option value="URBANRATS">UrbanRats (credited to the operator)</option>
                <option value="OPERATOR">The operator</option>
              </select>
            </F>
            <F label="Approval above (per currency, atomic)" hint="e.g. EUR 100000 — a period above this total waits for a second person">
              <input value={f.approval} onChange={(e) => setF({ ...f, approval: e.target.value })} placeholder="EUR 100000" />
            </F>
          </div>
          <h4>Prize steps (paid automatically when a period ends)</h4>
          <TiersEditor tiers={tiers} set={setTiers} currencyHint={currencies.map((c) => `${c} 500`).join(' ') || 'EUR 500'} />
          <F label={`Reason (min ${MIN_REASON} chars)`}>
            <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="board-reason" />
          </F>
          <div className="op-actions">
            <button type="submit" className="btn primary" disabled={!ok || f.name.trim().length < 2 || (f.scope !== 'GLOBAL' && !f.scopeId)} data-testid="board-save">
              Create leaderboard
            </button>
            <button type="button" className="btn ghost" onClick={() => setAdding(false)}>
              Cancel
            </button>
            {msg && <span className="op-msg bad">{msg}</span>}
          </div>
        </form>
      )}
    </Section>
  );
}

function Drops({ v, post, reload }: { v: View; post: Post; reload: () => Promise<void> }) {
  const [adding, setAdding] = useState(false);
  const [f, setF] = useState({ name: '', startsAt: '', durationHours: '24', scope: 'GLOBAL', scopeId: '*', mode: 'fixed', metric: 'ROUNDS', target: '10', maxOpens: '100', funding: 'URBANRATS' });
  const [pool, setPool] = useState([
    { metric: 'ROUNDS', min: 10, max: 25 },
    { metric: 'EXACT_HITS', min: 1, max: 3 },
  ]);
  const [prizes, setPrizes] = useState<{ weight: number; kind: string; count: number; values: string; boostBps: string }[]>([
    { weight: 70, kind: 'FREEBET', count: 1, values: '', boostBps: '' },
    { weight: 25, kind: 'FREEBET', count: 3, values: '', boostBps: '' },
    { weight: 5, kind: 'FREEBET', count: 10, values: '', boostBps: '' },
  ]);
  const { reason, setReason, ok } = useReason();
  const [msg, setMsg] = useState<string | null>(null);
  const { nowMs: now } = useCtx();
  return (
    <Section title={`Drop chests (${v.drops.filter((d) => d.status === 'ACTIVE' && new Date(d.endsAt).getTime() > now).length} running)`}>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Drop</th>
              <th>Goal</th>
              <th>Window</th>
              <th>Opened</th>
              <th>Where</th>
              <th>Fairness</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {v.drops.map((d) => {
              const live = d.status === 'ACTIVE' && new Date(d.endsAt).getTime() > now && new Date(d.startsAt).getTime() <= now;
              return (
                <tr key={d.dropId}>
                  <td>
                    <strong>{d.name}</strong>
                    <div>
                      <span className={`tag ${live ? 'st-ACCEPTED' : d.status === 'CANCELLED' ? 'st-VOIDED' : ''}`}>{live ? 'LIVE' : d.status === 'CANCELLED' ? 'CANCELLED' : new Date(d.startsAt).getTime() > now ? 'SCHEDULED' : 'ENDED'}</span>
                    </div>
                  </td>
                  <td>
                    {d.metric} ≥ {d.metric === 'BEST_MULTIPLIER' ? `×${(d.target / 100).toFixed(2)}` : d.target}
                    {d.randomized && <span className="muted"> (drawn)</span>}
                  </td>
                  <td className="muted">
                    {fmtTime(d.startsAt)} → {fmtTime(d.endsAt)}
                  </td>
                  <td>
                    {d.opens} / {d.maxOpens}
                  </td>
                  <td className="mono">{d.scope === 'GLOBAL' ? 'global' : d.scopeId}</td>
                  <td className="mono small" title={d.seed ? `seed ${d.seed}` : 'seed revealed after the end'}>
                    {d.seedCommitment.slice(0, 12)}…{d.seed ? ' · revealed' : ''}
                  </td>
                  <td>
                    {v.canManage && live && (
                      <button
                        type="button"
                        className="btn ghost"
                        onClick={async () => {
                          const r = prompt(`Cancel “${d.name}”? Reason (min ${MIN_REASON} chars):`);
                          if (!r || r.trim().length < MIN_REASON) return;
                          await post(`/v1/admin/engagement/drops/${d.dropId}/cancel`, { reason: r });
                          await reload();
                        }}
                      >
                        Cancel
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {v.canManage && !adding && (
        <button type="button" className="btn primary" onClick={() => setAdding(true)} data-testid="drop-new">
          + New drop chest
        </button>
      )}
      {v.canManage && adding && (
        <form
          className="op-form op-site-form"
          onSubmit={async (e) => {
            e.preventDefault();
            const r = await post('/v1/admin/engagement/drops', {
              name: f.name,
              startsAt: f.startsAt ? new Date(f.startsAt).toISOString() : undefined,
              durationHours: Number(f.durationHours),
              scope: f.scope,
              scopeId: f.scopeId,
              ...(f.mode === 'random' ? { randomPool: pool } : { metric: f.metric, target: Number(f.target) }),
              prizeTable: prizes.map((p) => ({ weight: p.weight, kind: p.kind, count: p.count, values: parseValues(p.values), boostBps: p.kind === 'BOOST' ? Number(p.boostBps) : undefined })),
              maxOpens: Number(f.maxOpens),
              funding: f.funding,
              reason,
            });
            setMsg(r.ok ? null : r.error);
            if (r.ok) {
              setAdding(false);
              setReason('');
              await reload();
            }
          }}
        >
          <h4>New drop chest</h4>
          <div className="op-grid">
            <F label="Name (players see it)">
              <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Akşam kasası" data-testid="drop-name" />
            </F>
            <F label="Starts" hint="empty = now">
              <input type="datetime-local" value={f.startsAt} onChange={(e) => setF({ ...f, startsAt: e.target.value })} />
            </F>
            <F label="Duration (hours)">
              <input inputMode="numeric" value={f.durationHours} onChange={(e) => setF({ ...f, durationHours: e.target.value })} />
            </F>
            <ScopePicker v={v} scope={f.scope} scopeId={f.scopeId} set={(s, id) => setF({ ...f, scope: s, scopeId: id })} />
            <F label="Chests in total" hint="first come, first served">
              <input inputMode="numeric" value={f.maxOpens} onChange={(e) => setF({ ...f, maxOpens: e.target.value })} />
            </F>
            <F label="Who pays">
              <select value={f.funding} onChange={(e) => setF({ ...f, funding: e.target.value })}>
                <option value="URBANRATS">UrbanRats</option>
                <option value="OPERATOR">The operator</option>
              </select>
            </F>
            <F label="Goal">
              <select value={f.mode} onChange={(e) => setF({ ...f, mode: e.target.value })}>
                <option value="fixed">Fixed goal</option>
                <option value="random">Drawn at random from a pool</option>
              </select>
            </F>
          </div>
          {f.mode === 'fixed' ? (
            <div className="op-grid">
              <F label="Statistic">
                <select value={f.metric} onChange={(e) => setF({ ...f, metric: e.target.value })}>
                  {v.dropMetrics.map((m) => (
                    <option key={m}>{m}</option>
                  ))}
                </select>
              </F>
              <F label="Target" hint={f.metric === 'BEST_MULTIPLIER' ? '× 100, e.g. 500 = win at ×5.00' : 'count within the drop window'}>
                <input inputMode="numeric" value={f.target} onChange={(e) => setF({ ...f, target: e.target.value })} />
              </F>
            </div>
          ) : (
            <div className="eng-tiers">
              <p className="note">The seed (committed now, revealed at the end) picks one line and a target in its range.</p>
              {pool.map((p, i) => (
                <div key={i} className="eng-tier">
                  <select value={p.metric} onChange={(e) => setPool(pool.map((x, j) => (j === i ? { ...x, metric: e.target.value } : x)))}>
                    {v.dropMetrics.map((m) => (
                      <option key={m}>{m}</option>
                    ))}
                  </select>
                  <input inputMode="numeric" value={p.min} onChange={(e) => setPool(pool.map((x, j) => (j === i ? { ...x, min: Number(e.target.value) || 1 } : x)))} aria-label="min" />
                  <span>–</span>
                  <input inputMode="numeric" value={p.max} onChange={(e) => setPool(pool.map((x, j) => (j === i ? { ...x, max: Number(e.target.value) || 1 } : x)))} aria-label="max" />
                  <button type="button" className="btn ghost" onClick={() => setPool(pool.filter((_, j) => j !== i))}>
                    ✕
                  </button>
                </div>
              ))}
              <button type="button" className="btn" onClick={() => setPool([...pool, { metric: 'WINS', min: 3, max: 8 }])}>
                + Pool line
              </button>
            </div>
          )}
          <h4>Prizes in the chest (chance by weight)</h4>
          <div className="eng-tiers">
            {prizes.map((p, i) => (
              <div key={i} className="eng-tier">
                <input inputMode="numeric" value={p.weight} onChange={(e) => setPrizes(prizes.map((x, j) => (j === i ? { ...x, weight: Number(e.target.value) || 1 } : x)))} aria-label="weight" title="weight" />
                <span className="muted">{Math.round((p.weight / prizes.reduce((s, x) => s + x.weight, 0)) * 100)} %</span>
                <select value={p.kind} onChange={(e) => setPrizes(prizes.map((x, j) => (j === i ? { ...x, kind: e.target.value } : x)))}>
                  <option value="FREEBET">Freebet</option>
                  <option value="BOOST">Boost</option>
                </select>
                <input inputMode="numeric" value={p.count} onChange={(e) => setPrizes(prizes.map((x, j) => (j === i ? { ...x, count: Number(e.target.value) || 1 } : x)))} aria-label="count" title="items" />
                {p.kind === 'FREEBET' ? (
                  <input value={p.values} placeholder="EUR 100 TRY 5000 (atomic)" onChange={(e) => setPrizes(prizes.map((x, j) => (j === i ? { ...x, values: e.target.value } : x)))} aria-label="values" />
                ) : (
                  <input inputMode="numeric" value={p.boostBps} placeholder="bps, 1000 = +10 %" onChange={(e) => setPrizes(prizes.map((x, j) => (j === i ? { ...x, boostBps: e.target.value } : x)))} aria-label="boost" />
                )}
                <button type="button" className="btn ghost" onClick={() => setPrizes(prizes.filter((_, j) => j !== i))}>
                  ✕
                </button>
              </div>
            ))}
            <button type="button" className="btn" onClick={() => setPrizes([...prizes, { weight: 10, kind: 'BOOST', count: 1, values: '', boostBps: '1000' }])}>
              + Prize
            </button>
          </div>
          <p className="note">Random prize draws can count as prize promotions in some countries: block those countries with the Drop chests switch.</p>
          <F label={`Reason (min ${MIN_REASON} chars)`}>
            <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="drop-reason" />
          </F>
          <div className="op-actions">
            <button type="submit" className="btn primary" disabled={!ok || f.name.trim().length < 2 || (f.scope !== 'GLOBAL' && !f.scopeId)} data-testid="drop-save">
              Create drop chest
            </button>
            <button type="button" className="btn ghost" onClick={() => setAdding(false)}>
              Cancel
            </button>
            {msg && <span className="op-msg bad">{msg}</span>}
          </div>
        </form>
      )}
    </Section>
  );
}

function RunCard({ r, v, post, reload }: { r: Run; v: View; post: Post; reload: () => Promise<void> }) {
  const { reason, setReason, ok } = useReason();
  return (
    <div className="card">
      <strong>{v.boards.find((b) => b.boardId === r.boardId)?.name ?? r.boardId}</strong>{' '}
      <span className="muted">
        {fmtTime(r.periodStart)} → {fmtTime(r.periodEnd)} · total {valuesText(r.totals)} · {r.funding === 'URBANRATS' ? 'UrbanRats pays' : 'operator pays'}
      </span>
      <ol className="eng-top">
        {r.winners.map((w) => (
          <li key={w.rank}>
            #{w.rank} {w.alias} <span className="muted">{w.operatorId}</span> · {w.count} × {w.kind} {w.faceValueAtomic ? `${w.currency} ${w.faceValueAtomic}` : ''} {w.skipped && <em className="muted">skipped: {w.skipped}</em>}
          </li>
        ))}
      </ol>
      {v.canApprove && (
        <div className="op-actions">
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder={`Reason (min ${MIN_REASON} chars)`} />
          <button type="button" className="btn primary" disabled={!ok} onClick={async () => (await post(`/v1/admin/engagement/prize-runs/${r.runId}/decide`, { approve: true, reason }), await reload())}>
            Approve and grant
          </button>
          <button type="button" className="btn danger" disabled={!ok} onClick={async () => (await post(`/v1/admin/engagement/prize-runs/${r.runId}/decide`, { approve: false, reason }), await reload())}>
            Reject
          </button>
        </div>
      )}
    </div>
  );
}
